"use client";

import React, { useCallback, useMemo, useState } from "react";
import { CheckSquare, Plus } from "lucide-react";
import { MODAL_IDS, useGlobalModalStack } from "@wtc/ui";
import { Button } from "@/components/ui/button";
import {
  EnhancedTabs,
  EnhancedTabsContent,
  EnhancedTabsList,
  EnhancedTabsTrigger,
} from "@/components/ui/tabs/enhanced-tabs";
import { ResponsiveSelect } from "@/components/ui/responsive-select";
import { useMediaQuery } from "@/lib/hooks/ui/use-media-query";
import { useTasks } from "@/lib/hooks/api/tasks";
import { useAdminUsers } from "@/lib/hooks/api/admin";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Tickets } from "@/src/gql/graphql";
import type { AppUser } from "@/lib/types/database";
import {
  TicketAssigneesList,
  TicketTasksAllTable,
  useAssigneeAggregates,
  type TaskRow,
  type TicketTasksSubTab,
} from "./TicketTasksTab/index";

/**
 * Ticket detail: tasks linked via `contact_tasks.ticket_id`, with sub-tabs
 * for the full list and for assignee profiles aggregated across those tasks.
 *
 * This file is a thin orchestrator. Presentational pieces live alongside it
 * in `./TicketTasksTab/`.
 */
export const TicketTasksTab = React.memo(({ item }: TabContentProps<Tickets>) => {
  const ticketId = item.id;
  const { openModal } = useGlobalModalStack();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [subTab, setSubTab] = useState<TicketTasksSubTab>("all");

  const { data: tasks = [], isLoading, error, refetch } = useTasks({ ticket_id: ticketId });
  const { data: allUsers = [] } = useAdminUsers();

  const ticketTitle =
    (item as { title?: string }).title ||
    ((item as { ticket_number?: number }).ticket_number != null
      ? `Ticket #${(item as { ticket_number?: number }).ticket_number}`
      : "this ticket");
  const contactId = (item as { contact_id?: string | null }).contact_id ?? undefined;

  const taskRows = (tasks || []) as TaskRow[];
  const assigneeAggregates = useAssigneeAggregates(taskRows, allUsers as AppUser[]);

  const handleCreateTask = useCallback(() => {
    openModal(MODAL_IDS.tasks.create, {
      ticketId,
      contactId,
    });
  }, [openModal, ticketId, contactId]);

  const handleSubTabChange = useCallback((value: string) => {
    setSubTab(value as TicketTasksSubTab);
  }, []);

  const subTabOptions = useMemo(
    () => [
      { value: "all", label: "All tasks" },
      { value: "assignees", label: "Assigned to" },
    ],
    [],
  );

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <CheckSquare className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-card-foreground">Tasks</h3>
        </div>
        <Button
          type="button"
          variant="default"
          size="sm"
          className="gap-2 w-fit"
          onClick={handleCreateTask}
        >
          <Plus className="w-4 h-4" />
          Add task
        </Button>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400">
        Tasks linked to {ticketTitle}. Use <strong>Assigned to</strong> to see everyone
        assigned on these tasks and which tasks they own.
      </p>

      {isMobile ? (
        <ResponsiveSelect
          value={subTab}
          onValueChange={handleSubTabChange}
          options={subTabOptions}
          placeholder="Section"
          mobileBreakpoint="(max-width: 768px)"
        />
      ) : null}

      <EnhancedTabs value={subTab} onValueChange={handleSubTabChange} className="w-full">
        {!isMobile ? (
          <EnhancedTabsList className="grid w-full max-w-md grid-cols-2">
            <EnhancedTabsTrigger value="all" variant="blue" className="text-sm">
              All tasks
            </EnhancedTabsTrigger>
            <EnhancedTabsTrigger value="assignees" variant="green" className="text-sm">
              Assigned to
            </EnhancedTabsTrigger>
          </EnhancedTabsList>
        ) : null}

        <EnhancedTabsContent value="all" className="mt-4">
          <TicketTasksAllTable
            tasks={taskRows}
            isLoading={isLoading}
            error={error}
            refetch={() => {
              void refetch();
            }}
          />
        </EnhancedTabsContent>

        <EnhancedTabsContent value="assignees" className="mt-4 space-y-4">
          <TicketAssigneesList aggregates={assigneeAggregates} isLoading={isLoading} />
        </EnhancedTabsContent>
      </EnhancedTabs>
    </div>
  );
});

TicketTasksTab.displayName = "TicketTasksTab";
